import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { mockSave } from '../services/botConfigService.js';

const MAX_LENGTH = 2000;

export function PersonalityPanel({ bot, updateBot, mockErrorOn, notify }) {
  const [text, setText] = useState(bot.instructions || '');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [testedText, setTestedText] = useState(bot.instructions || '');
  const dirty = text !== bot.instructions;
  const valid = text.trim().length > 0 && text.length <= MAX_LENGTH;
  const needsTest = text !== testedText;

  useEffect(() => {
    setText(bot.instructions || '');
    setTestedText(bot.instructions || '');
  }, [bot.instructions]);

  function validate() {
    if (!text.trim()) return 'Add instructions before saving.';
    if (text.length > MAX_LENGTH) return 'Instructions must be 2,000 characters or fewer.';
    return null;
  }

  function save() {
    const validation = validate();
    if (validation) { setErrorMsg(validation); return; }
    setErrorMsg(null);
    setStatus('saving');
    mockSave(mockErrorOn)
      .then(() => {
        updateBot({ instructions: text });
        setStatus('idle');
        notify('success', 'Instructions saved');
      })
      .catch(() => {
        setStatus('error');
        setErrorMsg(null);
      });
  }

  function testBot() {
    const validation = validate();
    if (validation) { setErrorMsg(validation); return; }
    setErrorMsg(null);
    setTestedText(text);
    setShowTest(true);
  }

  return (
    <div>
      <div className="panel-header">
        <h1>Personality &amp; instructions</h1>
        <p>This shapes how your bot talks, not what it knows — product facts still come from the Knowledge Base.</p>
      </div>
      {status === 'error' && <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />}
      <div className="card">
        <div className={'field' + (errorMsg ? ' invalid' : '')}>
          <label htmlFor="instructions">
            Instructions
            <span className={'counter' + (text.length > MAX_LENGTH ? ' danger' : '')}>{text.length} / {MAX_LENGTH}</span>
          </label>
          <textarea
            id="instructions"
            rows={6}
            maxLength={MAX_LENGTH + 1}
            placeholder="e.g. Be concise, avoid legal advice, always offer a demo link, never discuss competitor pricing."
            value={text}
            readOnly={status === 'saving'}
            aria-describedby="instructions-hint instructions-error"
            onChange={(e) => { setText(e.target.value); setErrorMsg(null); }}
          />
          {errorMsg && <div id="instructions-error" className="error-text"><Icon.alert s={12} /> {errorMsg}</div>}
          <div id="instructions-hint" className="hint">Stored separately from published knowledge and never shown to visitors directly.</div>
        </div>
        {needsTest && (
          <div className="alert-banner" style={{ background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.4)', color: '#8A6100' }}>
            <span className="msg"><Icon.alert s={13} /> Instructions changed — test the bot before publishing.</span>
          </div>
        )}
        <div className="actions-row">
          <button className="btn-primary" disabled={status === 'saving' || !valid || !dirty} onClick={save}>
            {status === 'saving' && <span className="spinner"></span>}
            {status === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
          <button className="btn-secondary" disabled={status === 'saving' || !valid} onClick={testBot}>Test bot</button>
        </div>
        {showTest && (
          <div className="card personality-test" role="status" aria-live="polite">
            <div className="preview-label">Bot test preview</div>
            <div className="bubble">Hello! I’m your ChatBizz assistant. How can I help you today?</div>
            <p className="hint" style={{ marginTop: 12, marginBottom: 0 }}><strong>Instructions being tested:</strong> {testedText}</p>
            <div className="actions-row"><button className="btn-secondary" onClick={() => setShowTest(false)}>Close preview</button></div>
          </div>
        )}
      </div>
    </div>
  );
}
